import type { TFunction } from 'i18next'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { roundAmount } from 'src/utils/roundAmount'

/**
 * One glyph vocabulary for the tiles, the settlement rings and (later) the
 * past-results row: solid disc + centre dot = holed, hollow ring + outside dot =
 * missed. Solid-vs-hollow reads in greyscale, so colour is a secondary cue only.
 */
export type PuttMark = 'holed' | 'missed'
export type TileKind = 'count' | 'exact' | 'round'

/** Matches `useClientSideTranslation`'s stabilised `t` (i18n/client.ts:54). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors useClientSideTranslation's stable t
export type Translate = (key?: string, options?: any) => string

/**
 * Structurally the board-local half of `PricedOption`
 * (`birdieMarkets.ts:6` = `MarketOptionConfig & { disabled: boolean }`), so B9
 * can hand `applyRoundOdds`' output straight to `buildTileViews`.
 */
export interface TileOption {
  id: string
  multiplier: number
  bettable: boolean
  disabled?: boolean
}

export interface BirdieTileView {
  optionId: string
  /** Canonical key: 'IN2' or 'HMH'. */
  key: string
  kind: TileKind
  /**
   * What the tile's glyph row draws. B13 split the two families: a COUNT tile
   * shows ONLY its holed dots (so `IN2` is two green dots, not two green and
   * one hollow), while an EXACT tile still shows all three putts in order. The
   * one exception is `IN0`, which has no holed dots and draws the three hollow
   * rings instead — "none of the three went in".
   */
  glyphs: PuttMark[]
  /** Putts holed. NOT `glyphs.filter(...)` any more — see `glyphs` above. */
  made: number
  /** Always `PUTTS_PER_ROUND`; a count tile no longer draws all three slots. */
  total: number
  label: string
  /**
   * The price the tile SHOWS: the round's, with the lightning boost when the
   * round revealed one and the inventory booster the option's chip claimed
   * (`ChipPlacementPanel.tsx:3045-3055` folds both into the one number too).
   */
  multiplier: number
  /**
   * The same price WITHOUT the inventory booster — the round's own, which is
   * what a price band means. B32's zones, its ghosts and its plan all read this
   * one, so a booster the player claimed by hand can never move a tile into or
   * out of an armed rule's band. Equal to `multiplier` on every tile that has
   * claimed nothing, which is every tile on a cash-only board.
   */
  roundMultiplier: number
  /** The claimed booster's percentage, or 0. Drives the price's own mark. */
  boosterPercent: number
  multiplierText: string
  /** Set only when a boost applies — rendered struck through under the headline. */
  baseMultiplierText?: string
  boosted: boolean
  disabled: boolean
  ariaLabel: string
}

export const COUNT_KEY_RE = /^IN[0-3]$/
export const PATTERN_KEY_RE = /^[HM]{3}$/
/**
 * B36: the round-type side bets. A third family, and deliberately not a third
 * shape — these sit in row 2 of the right column in the same 112 x 60 tile.
 *
 * They are a different KIND of bet, though, and the tile has to say so: exact
 * and any-order both resolve off what the golfer does, while the round type is
 * drawn from the round's seed before a ball is struck and settles the same
 * whether he holes three or none. A player who reads these as a third putting
 * market will feel cheated by a frost round where nothing dropped, so the tile
 * carries no putt glyphs at all — the ice / ember treatment is the whole tell.
 */
export const ROUND_KEY_RE = /^(FROST|FIRE)$/

/** Putts in a Birdie round. The denominator every count tile is read against. */
export const PUTTS_PER_ROUND = 3

/**
 * The ball's box on each family, REFERENCE px (B21: 15 px on exact tiles, 16 px
 * on the count bars — B20's 19.8 / 21.1 stepped down with the tiles, owner:
 * "think it can all be a bit smaller"). It lives here, beside the glyph
 * vocabulary, rather than as a ternary in `<BirdieTile>`, so the two numbers
 * are stated once and can be asserted; the gap between balls is a constant in
 * both families and stays in the stylesheet with the rest of the flex row.
 *
 * A count bar's ball is the LARGER of the two on purpose: the bar is the easy
 * market and its balls are its whole answer, where an exact tile's three balls
 * are a sequence and read as a group.
 */
export const BALL_SIZE_PX: Readonly<Record<TileKind, number>> = { exact: 15, count: 16, round: 0 }

/**
 * The round-type mark's box, REFERENCE px. Well clear of a count ball's 16: the flake and the flame
 * are line-and-silhouette shapes rather than a disc, and these two tiles are the rarest things on the
 * board, so the mark is the tile's subject rather than a label beside a price (owner 2026-09-16, "the
 * icon could be a little bigger"). `round` is 0 in BALL_SIZE_PX above because a round-type tile draws
 * no balls at all — the number lives here so neither is a magic literal.
 */
export const ROUND_MARK_PX = 22

/**
 * The multiplier text. Copied from ChipPlacementPanel.tsx:3072-3081 rather than
 * imported (the shared panel is never modified): `roundAmount` FLOORS to 2 dp so
 * the shown odds can never exceed the floored payout (QA-60088), and there is no
 * locale-aware multiplier formatter anywhere in the widget.
 */
export function formatMultiplierText(multiplier: number): string {
  return `${multiplier % 1 === 0 ? multiplier.toFixed(0) : roundAmount(multiplier, 2).toFixed(2)}x`
}

export function countFromKey(key: string): number | undefined {
  if (!COUNT_KEY_RE.test(key)) return undefined
  return Number(key.slice(2))
}

/**
 * Which of the two markets a canonical key belongs to, or undefined for a key
 * that is neither.
 *
 * Extracted from `buildTileViews` (B26) rather than restated: the bet-history
 * rows read the family off the SAME key the board tiles do, and two copies of
 * "a three-letter H/M word is the exact market" would be two places for the
 * vocabulary to drift.
 */
export function familyForKey(key: string): TileKind | undefined {
  if (PATTERN_KEY_RE.test(key)) return 'exact'
  if (COUNT_KEY_RE.test(key)) return 'count'
  if (ROUND_KEY_RE.test(key)) return 'round'
  return undefined
}

/**
 * The glyph row for a key.
 *
 * EXACT keys are unchanged: three marks, left to right = putt 1 to putt 3, and
 * the order IS the bet.
 *
 * COUNT keys changed in B13 (owner: "for those we don't need to show the 1/3 and
 * 2/3 … for two in, just show 2 green circles and not the empty etc, and for
 * none in, that could show 3 empty circles … it's a bit too busy and hard to
 * quickly understand"). A count bet says nothing about WHICH putts go in, so the
 * empty slots were carrying no information and were the main thing making the
 * two families look alike at a glance. `IN0` keeps three hollow rings because
 * "nothing" cannot be drawn as an absence of dots.
 */
export function glyphsForKey(key: string): PuttMark[] | undefined {
  // A round-type tile draws no putts: it does not describe any (see ROUND_KEY_RE).
  if (ROUND_KEY_RE.test(key)) return []
  if (PATTERN_KEY_RE.test(key)) {
    const marks: PuttMark[] = []
    for (let i = 0; i < key.length; i++) marks.push(key.charAt(i) === 'H' ? 'holed' : 'missed')
    return marks
  }
  const made = countFromKey(key)
  if (made === undefined) return undefined
  const marks: PuttMark[] = []
  if (made === 0) {
    for (let i = 0; i < PUTTS_PER_ROUND; i++) marks.push('missed')
    return marks
  }
  for (let i = 0; i < made; i++) marks.push('holed')
  return marks
}

export function buildTileViews(input: {
  options: readonly TileOption[]
  keyById: Readonly<Record<string, string>>
  t: Translate
  boostedIds?: ReadonlySet<string>
  boostPercent?: number
  levelBoostFactor?: number
  /**
   * optionId → the percentage of the inventory booster bound to that option's
   * chip (`birdieInventory.boosterPercentById`). Absent on a board that has
   * claimed nothing, which leaves every view byte-identical to B20's.
   */
  boosterPercentById?: Readonly<Record<string, number>>
}): BirdieTileView[] {
  const {
    options,
    keyById,
    t,
    boostedIds,
    boostPercent = 0,
    levelBoostFactor = 1,
    boosterPercentById
  } = input
  const views: BirdieTileView[] = []

  for (const option of options) {
    // A tile with no price this round is not bettable and not drawn (spec).
    if (!option.bettable) continue
    const key = keyById[option.id]
    if (key === undefined) continue
    const glyphs = glyphsForKey(key)
    if (glyphs === undefined) continue

    const kind = familyForKey(key)
    if (kind === undefined) continue
    const boosted = boostedIds !== undefined && boostedIds.has(option.id) && boostPercent > 0
    const base = option.multiplier * levelBoostFactor
    const roundMultiplier = boosted ? base * (1 + boostPercent / 100) : base
    const boosterPercent = boosterPercentById?.[option.id] ?? 0
    const shown =
      boosterPercent > 0 ? roundMultiplier * (1 + boosterPercent / 100) : roundMultiplier
    const multiplierText = formatMultiplierText(shown)
    // `getTranslatedMarbleColor` is typed against i18next's branded TFunction;
    // the board only ever needs the `(key, { ns }) => string` shape, which is
    // what the exported `Translate` alias documents.
    const label = getTranslatedMarbleColor(key, t as unknown as TFunction)
    // B13: a count tile's glyph row is only its HOLED dots, so the count comes
    // from the key; an exact tile's row is all three putts, so it can still be
    // counted. `total` is the round's three putts either way.
    let made = countFromKey(key) ?? 0
    if (kind === 'exact') {
      made = 0
      for (const glyph of glyphs) if (glyph === 'holed') made++
    }

    views.push({
      optionId: option.id,
      key,
      kind,
      glyphs,
      made,
      total: PUTTS_PER_ROUND,
      label,
      multiplier: shown,
      roundMultiplier,
      boosterPercent,
      multiplierText,
      baseMultiplierText: boosted ? formatMultiplierText(base) : undefined,
      boosted,
      disabled: option.disabled === true,
      // B11 item 9 / review 12: this WAS an English concatenation, so every
      // non-English screen-reader user heard "Zwei drin, 2 of 3 holed, pays
      // 2.53x". Both sentences are now literal `t()` keys (A25 — the extractor
      // is literal-only, and a computed key would never reach the 16 locales).
      //
      // An EXACT tile's label already spells the sequence out ("Hole Miss
      // Hole"), so it needs no glyph words of its own — which is also two fewer
      // strings to translate sixteen times.
      //
      // B13 took the "/3" off the tile but NOT out of the sentence: the count
      // tile still means "two of the three putts", and the key (and its sixteen
      // translations) stays exactly as it is — `total` is now the constant
      // rather than the glyph count, which the dots no longer report.
      ariaLabel:
        kind === 'count'
          ? t('{{label}}, {{made}} of {{total}} holed, pays {{price}}', {
              label,
              made,
              total: PUTTS_PER_ROUND,
              price: multiplierText
            })
          : t('{{label}}, pays {{price}}', { label, price: multiplierText })
    })
  }

  return views
}
